/* RetailVitamin global JS entrypoint */
/* All interactive components should attach under window.RV.components */

window.RV = window.RV || {
  config: {},
  components: {},
  utils: {},
};

RV.utils.debounce = function (fn, delay) {
  let timeout;
  return function (...args) {
    const ctx = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(ctx, args), delay);
  };
};

RV.initAll = function () {
  Object.values(RV.components).forEach((cmp) => {
    if (typeof cmp.init === 'function') {
      try {
        cmp.init();
      } catch (err) {
        console.error('[RV] Component init failed:', cmp.name || 'anonymous', err);
      }
    }
  });
};

/* Sticky header */

RV.components.StickyHeader = {
  name: 'StickyHeader',
  init() {
    const header = document.querySelector('[data-rv-sticky-header]');
    if (!header) return;

    const stickyClass = 'rv-header--sticky';
    const threshold = header.offsetHeight || 80;

    window.addEventListener('scroll', () => {
      if (window.scrollY > threshold) {
        header.classList.add(stickyClass);
      } else {
        header.classList.remove(stickyClass);
      }
    });
  },
};

/* Predictive search */

RV.components.SearchBar = {
  name: 'SearchBar',
  init() {
    const container = document.querySelector('[data-rv-search]');
    if (!container) return;

    const input = container.querySelector("input[type='search']");
    const panel = container.querySelector('[data-rv-search-results]');
    if (!input || !panel) return;

    const loadingClass = 'rv-search--loading';
    const openClass = 'rv-search--open';

    const doSearch = RV.utils.debounce(async (term) => {
      if (!term || term.trim().length < 2) {
        panel.innerHTML = '';
        container.classList.remove(openClass);
        input.setAttribute('aria-expanded', 'false');
        return;
      }

      container.classList.add(loadingClass);

      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(
          term,
        )}&resources[type]=product&resources[limit]=5&section_id=rv-predictive-search`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });

        if (!res.ok) {
          throw new Error('Search request failed');
        }

        const data = await res.json();
        const markup = data.sections?.['rv-predictive-search'] || '';
        panel.innerHTML = markup;
        container.classList.add(openClass);
        input.setAttribute('aria-expanded', 'true');
      } catch (err) {
        console.error('[RV] Predictive search error', err);
      } finally {
        container.classList.remove(loadingClass);
      }
    }, 200);

    input.addEventListener('input', (e) => {
      doSearch(e.target.value);
    });

    document.addEventListener('click', (e) => {
      if (!container.contains(e.target)) {
        container.classList.remove(openClass);
        container.classList.remove(loadingClass);
        input.setAttribute('aria-expanded', 'false');
      }
    });
  },
};

/* Simple multi item carousel for New & Trending */

RV.components.ProductCarousel = {
  name: 'ProductCarousel',
  init() {
    const carousels = document.querySelectorAll('[data-rv-carousel]');
    if (!carousels.length) return;

    carousels.forEach((carousel) => {
      const sectionRoot = carousel.closest('.new-trending') || carousel;
      const track = carousel.querySelector('[data-rv-carousel-track]');
      const prev = sectionRoot.querySelector('[data-rv-carousel-prev]');
      const next = sectionRoot.querySelector('[data-rv-carousel-next]');
      if (!track || !prev || !next) return;

      const cards = Array.from(track.children);
      if (!cards.length) return;

      let index = 0;

      // Cache card step (width + gap) and refresh only on resize
      let cachedStep = 0;
      const fallbackVisibleCount = Number(carousel.dataset.rvCarouselVisible || '1');

      function calcStep() {
        if (!cards[0]) return 0;
        const gap = parseFloat(getComputedStyle(track).gap || getComputedStyle(track).columnGap || 16);
        return cards[0].getBoundingClientRect().width + gap;
      }

      function getVisibleCount() {
        if (cachedStep <= 0) return Math.max(fallbackVisibleCount, 1);
        return Math.max(1, Math.floor((carousel.clientWidth + 1) / cachedStep));
      }

      function getMaxIndex() {
        return Math.max(cards.length - getVisibleCount(), 0);
      }

      function syncControlState() {
        const maxIndex = getMaxIndex();
        const prevDisabled = index <= 0;
        const nextDisabled = index >= maxIndex;
        prev.disabled = prevDisabled;
        next.disabled = nextDisabled;
        prev.setAttribute('aria-disabled', prevDisabled ? 'true' : 'false');
        next.setAttribute('aria-disabled', nextDisabled ? 'true' : 'false');
      }

      cachedStep = calcStep();
      window.addEventListener(
        'resize',
        () => {
          cachedStep = calcStep();
          index = Math.max(0, Math.min(index, getMaxIndex()));
          syncControlState();
        },
        { passive: true },
      );

      function scrollToIndex(idx) {
        track.scrollTo({ left: idx * cachedStep, behavior: 'smooth' });
        syncControlState();
      }

      function syncIndexFromScroll() {
        if (cachedStep > 0) index = Math.round(track.scrollLeft / cachedStep);
        index = Math.max(0, Math.min(index, getMaxIndex()));
        syncControlState();
      }

      track.addEventListener('scroll', syncIndexFromScroll, { passive: true });

      prev.addEventListener('click', (event) => {
        event.preventDefault();
        index = Math.max(index - 1, 0);
        scrollToIndex(index);
      });

      next.addEventListener('click', (event) => {
        event.preventDefault();
        const maxIndex = getMaxIndex();
        index = Math.min(index + 1, maxIndex);
        scrollToIndex(index);
      });

      syncControlState();
    });
  },
};

document.addEventListener('DOMContentLoaded', () => {
  RV.initAll();
});
